import { useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { CATEGORY_DIRECTIONS, type Category } from '@app/shared';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ApiRequestError, apiErrorMessage } from '@/lib/api';
import { useCreateCategory } from './queries';

const DIRECTION_LABELS: Record<(typeof CATEGORY_DIRECTIONS)[number], string> = {
  expense: 'Dépense',
  income: 'Recette',
};

/** « Frais de dossier » → `frais-de-dossier` : le code est stable et imposé par l'API, l'utilisateur ne voit que le libellé. */
function slugify(label: string): string {
  return label
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/^[0-9-]+/, '');
}

export function CategoryCreateButton({ onCreated }: { onCreated?: (category: Category) => void }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [direction, setDirection] = useState<(typeof CATEGORY_DIRECTIONS)[number]>('expense');
  const create = useCreateCategory();

  const code = slugify(label);
  const valid = label.trim() !== '' && code !== '';

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!valid) return;
    create.mutate(
      { code, label: label.trim(), direction },
      {
        onSuccess: (category) => {
          toast.success(`Catégorie « ${category.label} » créée`);
          setOpen(false);
          setLabel('');
          onCreated?.(category);
        },
        onError: (error) =>
          toast.error(
            error instanceof ApiRequestError && error.code === 'category_code_taken'
              ? 'Une catégorie de ce nom existe déjà.'
              : apiErrorMessage(error, 'Création impossible'),
          ),
      },
    );
  }

  return (
    <>
      <Button type="button" variant="outline" size="icon" aria-label="Ajouter une catégorie" onClick={() => setOpen(true)}>
        <Plus />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle catégorie</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="category-label">Libellé</Label>
              <Input id="category-label" autoFocus maxLength={120} value={label} onChange={(event) => setLabel(event.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="category-direction">Type</Label>
              <Select value={direction} onValueChange={(value) => setDirection(value as typeof direction)}>
                <SelectTrigger id="category-direction" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_DIRECTIONS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {DIRECTION_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Le type ne pourra plus être modifié une fois des écritures rattachées.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={!valid || create.isPending}>
                {create.isPending ? 'Création…' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
